import {
  Button,
  ButtonLevel,
  ButtonSize,
  ImpactBolt,
  Skeleton,
  Text,
  TextColor,
  TextVariant,
  Tooltip,
  TooltipTrigger,
} from '@vizit/atoms';
import { InfoCircleIcon } from '@vizit/icons';
import { cx } from '@vizit/theme';
import * as React from 'react';
import { containerClasses, getCountColor } from './styles';
import type { AssetCountStatus, AssetCountV2Props } from './types';

function getStatus(count: number, optimal: number): AssetCountStatus {
  return Math.abs(count - optimal) <= 1 ? 'success' : 'warning';
}

/**
 * Asset count metric card displaying the current asset count
 * relative to an optimal target. The count number is green when
 * within ±1 of optimal, warning/orange otherwise.
 *
 * @example
 * <AssetCountV2 count={8} optimal={7} impactLevel="high" />
 */
const AssetCountV2 = React.forwardRef<HTMLDivElement, AssetCountV2Props>((props, forwardedRef) => {
  const {
    count,
    optimal,
    impactLevel,
    isDisabled = false,
    isFocused = false,
    isLoading = false,
    className,
    ...other
  } = props;

  if (isLoading) {
    return (
      <div
        ref={forwardedRef}
        className={cx(containerClasses({ state: 'default' }), className)}
        data-testid="asset-count-v2"
        {...other}
      >
        <div className="flex items-start justify-between w-full">
          <Skeleton variant="square" className="h-5 w-24" />
          <Skeleton variant="square" className="h-7 w-28" />
        </div>
        <div className="flex flex-col gap-2">
          <Skeleton variant="square" className="h-[42px] w-16" />
          <Skeleton variant="square" className="h-3 w-28" />
        </div>
      </div>
    );
  }

  const status = getStatus(count, optimal);
  const state = isDisabled ? 'disabled' : isFocused ? 'focused' : 'default';

  return (
    <div
      ref={forwardedRef}
      className={cx(containerClasses({ state }), className)}
      data-testid="asset-count-v2"
      {...other}
    >
      <div className="flex items-start justify-between w-full">
        <div className="flex items-center gap-1">
          <Text
            variant={TextVariant.MonoXs}
            color={TextColor.Quaternary}
            className={isDisabled ? 'text-fg-disabled-subtle' : ''}
            showEllipsis={false}
          >
            ASSET COUNT
          </Text>
          <TooltipTrigger delay={200}>
            <Button
              aria-label="Asset count info"
              level={ButtonLevel.Ghost}
              size={ButtonSize.Xs}
              className="cursor-default p-0 h-auto min-h-0"
            >
              <InfoCircleIcon size={12} className={isDisabled ? 'text-fg-disabled-subtle' : 'text-quaternary'} />
            </Button>
            <Tooltip>
              Aim for {optimal} assets in your carousel. Having {optimal - 1}-{optimal + 1} earns this checkmark.
            </Tooltip>
          </TooltipTrigger>
        </div>
        <ImpactBolt level={impactLevel} hideUnfilled isDisabled={isDisabled} />
      </div>

      <div className="flex flex-col gap-0.5">
        <Text
          variant={TextVariant.MonoXl}
          elementType="span"
          color={TextColor.Inherit}
          className={getCountColor(status, isDisabled)}
          showEllipsis={false}
        >
          {count}
        </Text>
        <Text
          variant={TextVariant.MonoSm}
          color={TextColor.Tertiary}
          className={isDisabled ? 'text-fg-disabled-subtle' : ''}
          showEllipsis={false}
        >
          /{optimal} is optimal
        </Text>
      </div>
    </div>
  );
});

AssetCountV2.displayName = 'AssetCountV2';

export { AssetCountV2 };
export type { AssetCountV2Props };
